import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileOpenOutlinedIcon from '@mui/icons-material/FileOpenOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TableContainer,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chip from '../../../components/Chip/Chip';
import DefaultTable from '../../../components/DefaultTable/DefaultTable';
import TableToolbar from '../../../components/DefaultTable/TableToolbar/TableToolbar';
import GlobalSearch from '../../../components/GlobalSearch/GlobalSearch';
import ActivateModal from '../../../components/Modals/ActivateModal';
import CredentialsModal from '../../../components/Modals/CredentialsModal';
import DeactivateModal from '../../../components/Modals/DeactivateModal';
import DefaultModal from '../../../components/Modals/DefaultModal';
import SuccessModal from '../../../components/Modals/SuccessModal';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { TalentStatus } from '../../../enums/talentStatus';
import { TalentType } from '../../../enums/talentType';
import { useShowFilterOrSearch } from '../../../hooks/useShowFilterOrSearch';
import { useTableFunctions } from '../../../hooks/useTableFunctions';
import { HeadCell } from '../../../models/headCell';
import { Pagination } from '../../../models/pagination';
import { Talent } from '../../../models/talent';
import {
  ADMINISTRATION_TALENT_CREATE,
  ADMINISTRATION_TALENT_EDIT
} from '../../../router/constants';
import { talentService } from '../../../services/talentService';
import { userService } from '../../../services/userService';
import {
  createDownloadFile,
  removeEmptyObjectFields
} from '../../../utils/helpers';
import { getRoundStatusStyle } from '../../../utils/statusRoundStyle';
import AdminFilterTalents, { FilterTalentsForm } from './AdminFilterTalents';
import LoginAsModal from '../../../components/Modals/LoginAsModal';

const initialTalentsColumnsData: HeadCell[] = [
  {
    id: 'firstName',
    label: 'Talent',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.firstName + ' ' + talent.lastName
  },
  {
    id: 'accountType',
    label: 'Type',
    sortable: true,
    visible: true,
    render: (talent: Talent) => TalentType[talent.accountType]
  },
  {
    id: 'awPayrollId',
    label: 'AW Payroll ID',
    sortable: true,
    visible: true,
    render: (talent: Talent) => talent.awPayrollId
  },
  {
    id: 'createdOn',
    label: 'Date Created',
    sortable: true,
    visible: true,
    render: (talent: Talent) =>
      talent.createdOn && format(new Date(talent.createdOn), 'MMM do, yyyy')
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    visible: true,
    thStyle: { width: 200, textAlign: 'center' },
    tdStyle: { textAlign: 'center' },
    render: (talent: Talent) => (
      <Box sx={getRoundStatusStyle(TalentStatus[talent.status])}>
        {TalentStatus[talent.status]}
      </Box>
    )
  }
];

const AdminTalentsList = (): JSX.Element => {
  const initialFilterData: FilterTalentsForm = {
    talents: [],
    accountType: '',
    status: [],
    awPayrollId: '',
    startDate: null,
    endDate: null
  };
  const [paginationTalents, setPaginationTalents] = useState<
    Pagination<Talent> | undefined
  >(undefined);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [showFilters, showSearch, onFilterClick, onSearchClick] =
    useShowFilterOrSearch();
  const {
    columnsData,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    searchTerm,
    filterData,
    onRequestSort,
    onChangePage,
    onChangeRowsPerPage,
    onChangeColumnVisibility,
    onSearchChange,
    onFilterChange
  } = useTableFunctions<FilterTalentsForm>(
    initialTalentsColumnsData,
    initialFilterData,
    'firstName',
    'asc'
  );
  const [menuEl, setMenuEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(menuEl);
  const [selectedTalent, setSelectedTalent] = useState<Talent[]>([]);
  const [deleteModalData, setDeleteModalData] = useState<{
    open: boolean;
    user?: Talent;
  }>({ open: false });
  const [deactivateModalData, setDeactivateModalData] = useState<{
    open: boolean;
    user?: Talent;
  }>({ open: false });
  const [loginAsModalData, setLoginAsModalData] = useState<{
    open: boolean;
    user?: Talent[];
  }>({ open: false, user: [] });
  const [activateModalData, setActivateModalData] = useState<{
    open: boolean;
    user?: Talent;
  }>({ open: false });
  const [resendModalData, setResendModalData] = useState<{
    open: boolean;
    user: Talent[];
  }>({
    open: false,
    user: []
  });
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const fileAttachmentRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getTalents();
  }, [page, rowsPerPage, sortBy, sortType, filterData, searchTerm]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    if (openMenu) {
      setMenuEl(null);
    } else {
      setMenuEl(event.currentTarget);
    }
  };

  const downloadTemplate = () => {
    setMenuEl(null);
    talentService.exportTalentTemplate().then((response) => {
      createDownloadFile(response.data, 'talentTemplate');
    });
  };

  const onAttachFile = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      const bulkFile = fileList[0];
      const payload = new FormData();
      payload.set('file', bulkFile);
      talentService.uploadTalents(payload).then(() => {
        // TODO: response should return all talents with newly imported
      });
    }
    event.target.value = '';
  };

  const onImportTalents = () => {
    setMenuEl(null);
    fileAttachmentRef.current?.click();
  };

  const getTalents = () => {
    talentService
      .getTalents(generateFilterBody(), {
        params: {
          page: page,
          size: rowsPerPage,
          sort: `${sortBy},${sortType}`
        }
      })
      .then((response) => {
        setPaginationTalents(response.data);
        setLoading(false);
      });
  };

  const handleExport = () => {
    talentService.exportTalents(generateFilterBody()).then((response) => {
      createDownloadFile(response.data, 'talents');
    });
  };

  const generateFilterBody = () => {
    let body = removeEmptyObjectFields(filterData);
    if (body.talents) {
      body = {
        ...body,
        talents: filterData.talents
          ? filterData.talents.map((talent) => talent.id)
          : []
      };
    }
    if (searchTerm) {
      body = {
        ...body,
        searchTerm: searchTerm
      };
    }
    return body;
  };

  const showCheckBox = (talent: Talent) =>
    talent.status === TalentStatus.ACTIVE;

  const onRowSelect = (talent: Talent[]) => {
    setSelectedTalent(talent);
  };

  const resendCredentials = (talent: Talent[]) => {
    setResendModalData({ open: true, user: talent });
  };

  const onConfirmResendCredentials = () => {
    const talentIds = selectedTalent.map((talent) => talent.id);
    userService.sendCredentials(talentIds as number[]).then(() => {
      setResendModalData({ open: false, user: selectedTalent });
      setSuccessModal(true);
    });
  };

  const onConfirmLogin = () => {
    if (selectedTalent[0].id && selectedTalent[0].keycloakId) {
      userService
        .loginAsUser(selectedTalent[0].keycloakId, 'allworknow')
        .then(() => {
          setLoginAsModalData({ open: false, user: selectedTalent });
          location.reload();
        });
    }
  };

  const onEditProject = (event: React.MouseEvent<unknown>, talent: Talent) => {
    event.stopPropagation();
    navigate(`${ADMINISTRATION_TALENT_EDIT}/${talent.id}`);
  };

  const onActivateTalent = (
    event: React.MouseEvent<unknown>,
    talent: Talent
  ) => {
    event.stopPropagation();
    setActivateModalData({ open: true, user: talent });
  };

  const onDeactivateTalent = (
    event: React.MouseEvent<unknown>,
    talent: Talent
  ) => {
    event.stopPropagation();
    setDeactivateModalData({ open: true, user: talent });
  };

  const onConfirmStatusChange = (talent?: Talent) => {
    if (talent?.id) {
      userService
        .changeUserStatus({ id: talent.id, activate: !talent.active })
        .then(() => {
          setDeactivateModalData({ open: false, user: undefined });
          setActivateModalData({ open: false, user: undefined });
          getTalents();
        });
    }
  };

  const onConfirmDelete = (talent?: Talent) => {
    if (talent && talent.id) {
      talentService.deleteTalent(talent.id).then(() => {
        setDeleteModalData({ open: false, user: undefined });
        getTalents();
      });
    }
  };

  const onDeleteTalent = (event: React.MouseEvent<unknown>, talent: Talent) => {
    event.stopPropagation();
    setDeleteModalData({ open: true, user: talent });
  };

  const loginAs = (talent: Talent[]) => {
    setLoginAsModalData({ open: true, user: talent });
  };

  const onShowActivate = (talent: Talent) =>
    talent.status === TalentStatus.INACTIVE;

  const onShowDeactivate = (talent: Talent) =>
    talent.status === TalentStatus.ACTIVE;

  const onShowEditIcon = () => true;

  const onShowDelete = (talent: Talent) =>
    talent.status === TalentStatus.ONBOARDING;

  return (
    <>
      <Box display="flex" alignItems="center">
        <PageTitle>View All Talent</PageTitle>
        <Chip size={paginationTalents?.totalElements} />
        <Box flexGrow={1} />
        <Button
          sx={{ mb: 3, mr: 2 }}
          variant="outlined"
          disabled={!selectedTalent.length}
          onClick={() => resendCredentials(selectedTalent)}
        >
          Send Credentials
        </Button>
        <Button
          sx={{ height: '100%', mr: 3, mb: 3 }}
          variant="outlined"
          disabled={!selectedTalent.length || !(selectedTalent.length < 2)}
          onClick={() => loginAs(selectedTalent)}
        >
          Login As User
        </Button>
        <Button
          sx={{ mb: 3 }}
          onClick={() => navigate(ADMINISTRATION_TALENT_CREATE)}
        >
          Add New Talent
        </Button>
      </Box>
      <Box sx={{ display: 'none' }}>
        <input
          type="file"
          title="attachment"
          accept=".xls,.xlsx"
          ref={fileAttachmentRef}
          onChange={(event) => onAttachFile(event)}
        />
      </Box>
      <TableContainer component={Paper} sx={{ minHeight: '320px' }}>
        <TableToolbar
          viewTableColumnsProps={{
            columns: columnsData,
            onColumnClick: onChangeColumnVisibility
          }}
          onFilter={onFilterClick}
          onSearch={onSearchClick}
          onExport={handleExport}
          toolbarAction={
            <>
              <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={openMenu ? 'basic-menu' : undefined}
                aria-expanded={openMenu ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleMenuClick}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={menuEl}
                open={openMenu}
                onClose={handleMenuClick}
                MenuListProps={{
                  'aria-labelledby': 'basic-button'
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MenuItem onClick={downloadTemplate}>
                  <ListItemIcon>
                    <FileOpenOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download template</ListItemText>
                </MenuItem>
                <MenuItem onClick={onImportTalents}>
                  <ListItemIcon>
                    <CloudUploadOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Import template</ListItemText>
                </MenuItem>
              </Menu>
            </>
          }
        >
          {selectedTalent.length > 0 && (
            <Typography
              color="inherit"
              variant="subtitle1"
              component="div"
              mb={1}
            >
              {selectedTalent.length} selected
            </Typography>
          )}
          {showSearch && (
            <GlobalSearch
              onSearchChange={onSearchChange}
              searchTerm={searchTerm}
            />
          )}
          {showFilters && (
            <AdminFilterTalents
              handleFormSubmit={onFilterChange}
              filterData={filterData}
            />
          )}
        </TableToolbar>
        <DefaultTable
          columns={columnsData}
          data={paginationTalents?.content}
          sortProps={{ sortBy, sortType, onRequestSort }}
          checkBoxes={{
            showCheckBox,
            selectRows: onRowSelect
          }}
          paginationProps={{
            page,
            rowsPerPage,
            onChangePage,
            onChangeRowsPerPage,
            totalElements: paginationTalents?.totalElements
          }}
          actionProps={{
            onEdit: onEditProject,
            onDeactivate: onDeactivateTalent,
            onActivate: onActivateTalent,
            onDelete: onDeleteTalent,
            showDeactivateButton: onShowDeactivate,
            showActivateButton: onShowActivate,
            showEditButton: onShowEditIcon,
            showDeleteButton: onShowDelete
          }}
        />
        {!loading && !paginationTalents?.content.length && (
          <Typography variant="h5" align="center" m={4}>
            No talents found!
          </Typography>
        )}
      </TableContainer>
      <CredentialsModal
        modalData={resendModalData}
        closeModal={() => setResendModalData({ open: false, user: [] })}
        confirmModal={onConfirmResendCredentials}
      />
      <SuccessModal
        open={successModal}
        handleClose={() => setSuccessModal(false)}
        message="You have successfully re-sent login credentials"
      />
      <DeactivateModal
        modalData={deactivateModalData}
        closeModal={() => setDeactivateModalData({ open: false })}
        confirmModal={() => onConfirmStatusChange(deactivateModalData.user)}
      />
      <ActivateModal
        modalData={activateModalData}
        closeModal={() => setActivateModalData({ open: false })}
        confirmModal={() => onConfirmStatusChange(activateModalData.user)}
      />
      <LoginAsModal
        modalData={loginAsModalData}
        closeModal={() => setLoginAsModalData({ open: false, user: [] })}
        confirmModal={onConfirmLogin}
      />
      <DefaultModal
        open={deleteModalData.open}
        handleClose={() => setDeleteModalData({ open: false, user: undefined })}
      >
        <Box sx={{ px: 6 }}>
          <Typography variant="h4" textAlign="center" color="grey.900" mb={2}>
            {`The user ${deleteModalData.user?.firstName} ${deleteModalData.user?.lastName} will be delete!`}
          </Typography>
          <Typography textAlign="center">
            Are you sure you want to continue?
          </Typography>
        </Box>
        <DialogActions sx={{ justifyContent: 'center', py: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteModalData({ open: false, user: undefined })}
          >
            Cancel
          </Button>
          <Button onClick={() => onConfirmDelete(deleteModalData.user)}>
            Continue
          </Button>
        </DialogActions>
      </DefaultModal>
    </>
  );
};

export default AdminTalentsList;
